import { useCssVariable } from '@library/hooks'
import { isDefined } from 'common/helpers'
import type { Nullable } from 'common/types'
import { scaleLinear, select } from 'd3'
import type { FC } from 'react'
import { useEffect, useRef, useState } from 'react'

import { DEFAULT_BOUNDARY, END_GRADIENT_OFFSET } from '../constants'
import { useLegendContext } from '../context'
import { getAxisCreateFunc, getTicks, getTransformAxis, updateTickLabel } from '../helpers'
import type { AxisProps } from '../types'
import css from './Axis.css'

export const Axis: FC<AxisProps> = ({
  boundary: entityBoundary,
  axisPosition,
  step,
  tickCount,
}) => {
  const [axisHeight, setAxisHeight] = useState<number>(0)
  const [axisWidth, setAxisWidth] = useState<number>(0)
  const ref = useRef<Nullable<SVGSVGElement>>(null)

  const { ['--main-font-size']: mainFontSizeCss } = useCssVariable('--main-font-size')
  const { attribute, height, width, entityId, isHorizontal } = useLegendContext()

  useEffect(() => {
    if (!ref.current) return

    const transform = getTransformAxis(isHorizontal, axisHeight, axisWidth, axisPosition)
    select(ref.current).attr('transform', transform)
  }, [entityBoundary, axisPosition, isHorizontal, axisHeight, axisWidth])

  useEffect(() => {
    const boundary = isDefined(entityId) ? entityBoundary : DEFAULT_BOUNDARY
    if (!boundary || !ref.current) return

    const container = select(ref.current)
    const scaleRange = isHorizontal ? width : height
    const scale = scaleLinear()
      .range([scaleRange - END_GRADIENT_OFFSET - 3, 0])
      .domain([boundary.start, boundary.stop])

    const fontSize = Number(/\d+/.exec(mainFontSizeCss)?.[0] ?? 10)
    const tickValues = getTicks(boundary, attribute, scaleRange, scale, fontSize, step, tickCount)
    const axisCreateFunc = getAxisCreateFunc(
      scale,
      tickValues,
      attribute,
      isHorizontal,
      axisPosition,
    )

    container.call(axisCreateFunc).call(axis => axis.select('.domain').remove())

    container
      .classed(css.horizontalLegendAxis, isHorizontal)
      .classed(css.oneHorizontalElement, isHorizontal && tickValues.length === 1)
      .classed(css.legendAxis, !isHorizontal)
      .classed(css.oneElement, !isHorizontal && tickValues.length === 1)
      .classed(css.defaultTextColor, !isDefined(entityId))
    updateTickLabel(ref.current, width, isHorizontal, axisPosition)

    const boundingRect = ref.current.getBoundingClientRect()
    if (isHorizontal) {
      setAxisHeight(boundingRect.height)
      setAxisWidth(width)
    } else {
      setAxisHeight(height)
      setAxisWidth(boundingRect.width)
    }
  }, [
    entityBoundary,
    attribute,
    isHorizontal,
    axisPosition,
    step,
    tickCount,
    width,
    height,
    mainFontSizeCss,
    entityId,
  ])

  return (
    <svg width={axisWidth} height={axisHeight}>
      <g ref={ref} id="legendAxis" />
    </svg>
  )
}
